import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import moment from 'moment'

import { ROUTES } from 'config/routes'
import { useSnackbar } from 'components/Snackbar/useSnackbar'
import { DELIVERY_WORK_QUERY_KEY, DELIVERY_WORK_ACTION } from 'constants/deliveryWorkQueryParams'
import type { ComboBoxOption, ComboBoxChange } from 'components/ComboBox'
import { deliveryReportStorage } from 'repositories/DeliveryReportStorage'
import { getDeliveryReportTenant } from 'repositories/ShipmentApi/getDeliveryReportTenant'
import { formatDeliveryReportTenantName } from 'repositories/ShipmentApi/getDeliveryReportTenant/formatTenantName'
import type { DeliveryTenant } from 'repositories/ShipmentApi/getDeliveryReportTenant/type'
import { getDeliveryWorkTypeSettings } from 'repositories/ShipmentApi/getDeliveryWorkTypeSettings'
import type { DeliveryWorkTypeSetting } from 'repositories/ShipmentApi/getDeliveryWorkTypeSettings/type'
import { createShipment } from 'repositories/ShipmentApi/createShipment'
import { searchShipments } from 'repositories/ShipmentApi/searchShipments'
import { getShipment } from 'repositories/ShipmentApi/getShipment'
import type { ShipmentDetail } from 'repositories/ShipmentApi/getShipment/type'
import { updateShipmentStatus } from 'repositories/ShipmentApi/updateShipmentStatus'
import { addShipmentVisit } from 'repositories/ShipmentApi/addShipmentVisit'
import { deleteShipmentVisit } from 'repositories/ShipmentApi/deleteShipmentVisit'
import { updateShipmentVisitOrder } from 'repositories/ShipmentApi/updateShipmentVisitOrder'
import { updateShipmentVisitDelivery } from 'repositories/ShipmentApi/updateShipmentVisitDelivery'
import { recordShipmentVisitTime } from 'repositories/ShipmentApi/recordShipmentVisitTime'
import type { VisitAction } from 'repositories/ShipmentApi/recordShipmentVisitTime/type'
import { getBookingCandidates } from 'repositories/ShipmentApi/bookingCandidates'
import type { BookingCandidate } from 'repositories/ShipmentApi/bookingCandidates/type'
import { updatePickBooking } from 'repositories/ShipmentApi/updatePickBooking'
import { TruckbookApiResponseError } from 'repositories/TruckbookApiResponseError'
import { getTruckbookApiErrorMessage } from 'repositories/getTruckbookApiErrorMessage'

import type { Visit } from '../utils/deliveryWorkMappers'
import {
  toDeliveryDestinationOptions,
  toManualDeliveryHistoryOptions,
  isManualHistoryOptionValue,
  toStamps,
  toVisit,
  mergeSavedVisit,
} from '../utils/deliveryWorkMappers'

type PageState = 'loading' | 'no_shipment' | 'has_shipment' | 'invalid_uid' | 'network_error'

export type CurrentShipment = {
  shipmentUid: string
  detail: ShipmentDetail
}

const SHIPMENT_STATUS_DELIVERING = 1 as const
const SHIPMENT_STATUS_COMPLETED = 2 as const

const ACTION_BY_TYPE: Record<'checkin' | 'start' | 'end' | 'checkout', VisitAction> = {
  checkin: 'checkin',
  start: 'start_work',
  end: 'end_work',
  checkout: 'checkout',
}

const formatBookingLabel = (c: BookingCandidate): string => {
  const time = `${moment(c.startAt, 'YYYY-MM-DD HH:mm:ss').format('MM月DD日 HH:mm')} ~ ${
    c.endAt != null ? moment(c.endAt, 'YYYY-MM-DD HH:mm:ss').format('HH:mm') : ''
  }`
  return c.bookingNo ? `${time}　${c.bookingNo}` : time
}

export const useSpDeliveryWorkController = () => {
  const { deliveryReportUid } = useParams<{ deliveryReportUid: string }>()
  const history = useHistory()
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const [isStartNew] = useState(
    () => searchParams.get(DELIVERY_WORK_QUERY_KEY) === DELIVERY_WORK_ACTION.START_SHIPMENT
  )
  const [shipmentUidFromUrl] = useState(() => searchParams.get('shipmentUid') ?? undefined)
  const { snackbar, notify } = useSnackbar()
  const [pageState, setPageState] = useState<PageState>('loading')
  const [tenantName, setTenantName] = useState('')
  const [deliveryTenants, setDeliveryTenants] = useState<DeliveryTenant[]>([])
  // 付帯作業マスタ（テナント共通）。init で一度だけ取得し、全 visit のチップ描画に使う
  const [workTypeSettings, setWorkTypeSettings] = useState<DeliveryWorkTypeSetting[]>([])
  // 手入力で決定した納品先名の履歴（新しい順・最大3件）。ローカルストレージから復元し、決定成功時に更新する
  const [manualDeliveryHistory, setManualDeliveryHistory] = useState<string[]>(() =>
    deliveryReportStorage.getManualDeliveryDestinations()
  )
  const [currentShipment, setCurrentShipment] = useState<CurrentShipment | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [pickBookingId, setPickBookingId] = useState<string | null>(null)
  const [isDeliveryProcessing, setIsDeliveryProcessing] = useState(false)
  const [confirmType, setConfirmType] = useState<'complete' | 'cancel' | null>(null)
  const [isStamping, setIsStamping] = useState(false)
  // 追加 / 削除 / 並び替え / 納品先登録の送信中ガード（連打による多重 API 実行を防ぐ）
  const [isMutatingVisit, setIsMutatingVisit] = useState(false)
  const [visits, setVisits] = useState<Visit[]>([])
  const [pickBookingCandidates, setPickBookingCandidates] = useState<BookingCandidate[]>([])
  const [isCandidatesLoading, setIsCandidatesLoading] = useState(false)
  const [isPickBookingUpdating, setIsPickBookingUpdating] = useState(false)
  // 各 visit の作業詳細フォームの未保存状態を集約する（ページ離脱ガード用）
  const [dirtyVisitMap, setDirtyVisitMap] = useState<Record<string, boolean>>({})

  const masterDeliveryOptions = useMemo(
    () => toDeliveryDestinationOptions(deliveryTenants),
    [deliveryTenants]
  )
  // マスタ label 集合。マスタと同名の手入力履歴を候補から除外して二重表示を防ぐ
  const masterDeliveryLabels = useMemo(
    () => new Set(masterDeliveryOptions.map((o) => o.label)),
    [masterDeliveryOptions]
  )
  const manualHistoryOptions = useMemo(
    () => toManualDeliveryHistoryOptions(manualDeliveryHistory, masterDeliveryLabels),
    [manualDeliveryHistory, masterDeliveryLabels]
  )
  // 候補は「手入力履歴 → 区切り線 → マスタ」の順で並べる（区切り線は ComboBox 側で両群がある時のみ描画）
  const deliveryDestinationOptions = useMemo(
    () => [...manualHistoryOptions, ...masterDeliveryOptions],
    [manualHistoryOptions, masterDeliveryOptions]
  )
  // 区切り線描画のため、手入力履歴グループの value 集合を ComboBox に渡す
  const deliveryHistoryValues = useMemo(
    () => new Set(manualHistoryOptions.map((o) => o.value)),
    [manualHistoryOptions]
  )
  // 再描画ハンドラ（useCallback）からは最新の選択肢を参照したいが、deps に入れると
  // ハンドラ参照が頻繁に変わるため ref 経由で読む。
  const deliveryOptionsRef = useRef<ComboBoxOption[]>(deliveryDestinationOptions)
  deliveryOptionsRef.current = deliveryDestinationOptions
  // 同期的な再入ガード（state は再レンダまで反映されず連打を防げないため ref で持つ）。
  // 打刻と visit 操作は相互に排他する（実行中の他方を弾く）。
  const isMutatingVisitRef = useRef(false)
  const isStampingRef = useRef(false)

  const reportVisitDirty = useCallback((visitId: string, dirty: boolean) => {
    setDirtyVisitMap((prev) => (prev[visitId] === dirty ? prev : { ...prev, [visitId]: dirty }))
  }, [])

  const hasUnsavedChanges = Object.values(dirtyVisitMap).some(Boolean)

  // ブラウザ離脱（リロード / タブ閉じ / 外部遷移）時に未保存があればブラウザ標準の確認を出す
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const handlePickBookingChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!currentShipment || isPickBookingUpdating) return
      const nextPickBookingId = e.target.value || null
      const driverPhone = deliveryReportStorage.getDriverPhone()
      if (!driverPhone) return

      setIsPickBookingUpdating(true)
      try {
        await updatePickBooking({
          shipmentUid: currentShipment.shipmentUid,
          deliveryReportUid,
          driverPhone,
          pickBookingId: nextPickBookingId,
        })
        setPickBookingId(nextPickBookingId)
      } catch (error) {
        notify({ message: getTruckbookApiErrorMessage(error), variant: 'error' })
      } finally {
        setIsPickBookingUpdating(false)
      }
    },
    [currentShipment, isPickBookingUpdating, deliveryReportUid, notify]
  )

  // ComboBox の選択 / 手入力をローカル state に反映する（API 送信は「決定」ボタン押下時）。
  // 手入力履歴（接頭辞付き value）を選んだ場合はマスタ選択ではなく手入力扱いに戻す（tenant_id を持たせない）。
  const handleDeliveryTenantChange = useCallback((visitId: string, next: ComboBoxChange) => {
    const deliveryTenantId = isManualHistoryOptionValue(next.value) ? null : next.value
    setVisits((prev) =>
      prev.map((v) =>
        v.id === visitId ? { ...v, deliveryTenantId, deliveryTenantLabel: next.label } : v
      )
    )
  }, [])

  // getShipment の visit を sequence_order 昇順に整列して Visit[] に変換する。
  // moveVisit は配列インデックスから移動先 sequence_order を算出するため、整列を前提にする。
  const buildVisits = useCallback(
    (detailVisits: ShipmentDetail['visits']): Visit[] =>
      [...detailVisits]
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        .map((v) => toVisit(v, deliveryOptionsRef.current)),
    []
  )

  // 操作後の再描画は shipment 取得 API のレスポンスで全置換する。
  // 追加 / 削除 / 並び替えは visit 集合・順序が変わるため全置換が必要（納品先登録・解除は単一 visit 更新で済む）。
  const reloadShipmentVisits = useCallback(
    async (shipmentUid: string) => {
      const res = await getShipment({ shipmentUid, deliveryReportUid })
      setCurrentShipment({ shipmentUid, detail: res.data.shipment })
      setVisits(buildVisits(res.data.shipment.visits))
    },
    [deliveryReportUid, buildVisits]
  )

  // visit 系 mutation（追加 / 削除 / 並び替え / 納品先登録・解除）の共通処理。
  // 再入ガード（ref）・isMutatingVisit のトグル・成功時の再描画 / トースト・エラー通知を集約する。
  // opts.reload=false のときは action 内で個別に state 更新する想定で getShipment を呼ばない。
  const runVisitMutation = useCallback(
    async (
      action: (ctx: { shipmentUid: string; driverPhone: string }) => Promise<unknown>,
      opts: { successMessage?: string; reload?: boolean } = {}
    ) => {
      const driverPhone = deliveryReportStorage.getDriverPhone()
      // 打刻中・他 mutation 実行中は弾く（連打・打刻との競合防止）
      if (!driverPhone || !currentShipment || isMutatingVisitRef.current || isStampingRef.current)
        return
      const { shipmentUid } = currentShipment
      isMutatingVisitRef.current = true
      setIsMutatingVisit(true)
      try {
        await action({ shipmentUid, driverPhone })
        if (opts.reload !== false) await reloadShipmentVisits(shipmentUid)
        if (opts.successMessage) notify({ message: opts.successMessage, variant: 'success' })
      } catch (error) {
        notify({ message: getTruckbookApiErrorMessage(error), variant: 'error' })
      } finally {
        isMutatingVisitRef.current = false
        setIsMutatingVisit(false)
      }
    },
    [currentShipment, notify, reloadShipmentVisits]
  )

  const handleAddVisit = useCallback(
    () =>
      runVisitMutation(({ shipmentUid, driverPhone }) =>
        addShipmentVisit({ shipmentUid, deliveryReportUid, driverPhone })
      ),
    [runVisitMutation, deliveryReportUid]
  )

  const handleDeleteVisit = useCallback(
    (visitId: string) =>
      runVisitMutation(({ shipmentUid, driverPhone }) =>
        deleteShipmentVisit({ shipmentUid, visitId, deliveryReportUid, driverPhone })
      ),
    [runVisitMutation, deliveryReportUid]
  )

  // 対象 visit の移動先 sequence_order（1..N）だけを送る。再採番は BE 側で行う。
  // visits は buildVisits で sequence_order 昇順・連番に整列済みのため、配列インデックスで算出する。
  // 端（先頭の上移動 / 末尾の下移動）は範囲外のため API を呼ばずに抜ける。
  const moveVisit = useCallback(
    (visitId: string, direction: 'up' | 'down') => {
      const idx = visits.findIndex((v) => v.id === visitId)
      if (idx < 0) return
      if (direction === 'up' && idx <= 0) return
      if (direction === 'down' && idx >= visits.length - 1) return
      // 0-based idx の要素は 1-based では idx+1 番目。上移動で idx 番目、下移動で idx+2 番目へ。
      const sequenceOrder = direction === 'up' ? idx : idx + 2
      return runVisitMutation(({ shipmentUid, driverPhone }) =>
        updateShipmentVisitOrder({
          shipmentUid,
          visitId,
          deliveryReportUid,
          driverPhone,
          sequenceOrder,
        })
      )
    },
    [visits, runVisitMutation, deliveryReportUid]
  )

  const handleMoveVisitUp = useCallback((visitId: string) => moveVisit(visitId, 'up'), [moveVisit])
  const handleMoveVisitDown = useCallback(
    (visitId: string) => moveVisit(visitId, 'down'),
    [moveVisit]
  )

  // 納品先更新 PATCH レスポンスの単一 visit を該当行にだけ反映する（他 visit の編集中ドラフトを保持）。
  // 納品先更新 PATCH のレスポンスには作業詳細（deliveryWork / 添付）が含まれないため、
  // toVisit で作り直しつつ作業詳細は既存値を据え置く（決定 / 解除で作業詳細・添付が消えるのを防ぐ）。
  const applyUpdatedVisit = useCallback((updated: ShipmentDetail['visits'][number]) => {
    setVisits((prev) =>
      prev.map((v) => {
        if (v.id !== String(updated.id)) return v
        const next = toVisit(updated, deliveryOptionsRef.current)
        return {
          ...next,
          deliveryWork: v.deliveryWork,
          deliveryWorkAttachments: v.deliveryWorkAttachments,
        }
      })
    )
  }, [])

  // 「決定」ボタン押下: ComboBox の選択 / 手入力を納品先更新 API で永続化する。
  // マスタ選択 = deliveryTenantId、手入力 = deliveryDestination。
  // 空（未選択・未入力）の「決定」は無視する（紐付け解除は専用のクリアボタンで行う）。
  const handleSelectDelivery = useCallback(
    (visitId: string) => {
      const visit = visits.find((v) => v.id === visitId)
      if (!visit) return
      const label = visit.deliveryTenantLabel.trim()
      const deliveryTenantId =
        visit.deliveryTenantId !== null ? Number(visit.deliveryTenantId) : null
      if (deliveryTenantId === null && label === '') return
      const deliveryDestination = deliveryTenantId === null ? label : undefined
      return runVisitMutation(
        async ({ shipmentUid, driverPhone }) => {
          const res = await updateShipmentVisitDelivery({
            shipmentUid,
            visitId,
            deliveryReportUid,
            driverPhone,
            deliveryTenantId: deliveryTenantId ?? undefined,
            deliveryDestination,
          })
          // PATCH レスポンスの visit をそのまま反映（余分な getShipment を避ける）
          applyUpdatedVisit(res.data.shipmentVisit)
          // 手入力で決定した納品先名はローカル履歴に保存し、次回以降の候補に出す。
          // ただし DB 登録済み（マスタと同名）の納品先は履歴に保存しない（マスタ候補で出るため）。
          if (deliveryDestination && !masterDeliveryLabels.has(deliveryDestination)) {
            setManualDeliveryHistory(
              deliveryReportStorage.addManualDeliveryDestination(deliveryDestination)
            )
          }
        },
        { successMessage: '納品先を登録しました', reload: false }
      )
    },
    [visits, runVisitMutation, deliveryReportUid, applyUpdatedVisit, masterDeliveryLabels]
  )

  // 確定済み納品先の右のクリアアイコン押下: その場で紐付けを解除する（決定ボタン不要）。
  // tenant_id / destination を送らず BE に両カラムを NULL クリアさせ、レスポンスの visit を反映する。
  const handleClearDelivery = useCallback(
    (visitId: string) =>
      runVisitMutation(
        async ({ shipmentUid, driverPhone }) => {
          const res = await updateShipmentVisitDelivery({
            shipmentUid,
            visitId,
            deliveryReportUid,
            driverPhone,
          })
          applyUpdatedVisit(res.data.shipmentVisit)
        },
        { successMessage: '納品先を解除しました', reload: false }
      ),
    [runVisitMutation, deliveryReportUid, applyUpdatedVisit]
  )

  const handleStampClick = useCallback(
    async (visitId: string, type: 'checkin' | 'start' | 'end' | 'checkout') => {
      const driverPhone = deliveryReportStorage.getDriverPhone()
      if (!driverPhone) return
      if (!currentShipment) return
      // 打刻中・visit 操作中は弾く（連打・visit 操作との競合防止）
      if (isStampingRef.current || isMutatingVisitRef.current) return

      const action = ACTION_BY_TYPE[type]
      isStampingRef.current = true
      setIsStamping(true)
      try {
        const res = await recordShipmentVisitTime({
          shipmentUid: currentShipment.shipmentUid,
          visitId,
          deliveryReportUid,
          driverPhone,
          action,
        })
        // 打刻結果（stamps）のみ反映する。納品先の編集中ドラフトを保持するため全置換しない。
        setVisits((prev) =>
          prev.map((v) =>
            v.id === visitId ? { ...v, stamps: toStamps(res.data.shipmentVisit) } : v
          )
        )
      } catch (error) {
        const message =
          error instanceof TruckbookApiResponseError
            ? error.message || 'エラーが発生しました'
            : '通信エラーが発生しました'
        notify({ message, variant: 'error' })
      } finally {
        isStampingRef.current = false
        setIsStamping(false)
      }
    },
    [currentShipment, deliveryReportUid, notify]
  )

  const handleCompleteDelivery = useCallback(() => {
    setConfirmType('complete')
  }, [])

  const handleCancelDeliveryComplete = useCallback(() => {
    setConfirmType('cancel')
  }, [])

  const handleDeliveryConfirmOk = useCallback(async () => {
    const type = confirmType
    setConfirmType(null)
    if (!type || !currentShipment) return

    const newStatus = type === 'complete' ? SHIPMENT_STATUS_COMPLETED : SHIPMENT_STATUS_DELIVERING
    setIsDeliveryProcessing(true)
    try {
      await updateShipmentStatus({
        shipmentUid: currentShipment.shipmentUid,
        deliveryReportUid,
        status: newStatus,
      })
      const res = await getShipment({
        shipmentUid: currentShipment.shipmentUid,
        deliveryReportUid,
      })
      setCurrentShipment({ shipmentUid: currentShipment.shipmentUid, detail: res.data.shipment })
      // visits は更新しない。ステータス変更で visit の集合・順序は変わらず、
      // 納品先 ComboBox の編集中ドラフトを保持するため。
    } catch (error) {
      notify({ message: getTruckbookApiErrorMessage(error), variant: 'error' })
    } finally {
      setIsDeliveryProcessing(false)
    }
  }, [confirmType, currentShipment, deliveryReportUid, notify])

  const handleDeliveryConfirmClose = useCallback(() => {
    setConfirmType(null)
  }, [])

  // 作業詳細の保存成功後に呼ばれる。shipment を再取得し（描画の単一ソース化・観点3）、
  // 保存した visit の作業記録だけを反映する。全 visit は上書きせず、編集途中の他 visit を保護する。
  const handleDeliveryWorkSaved = useCallback(
    async (savedVisitId: number) => {
      if (!currentShipment) return
      const res = await getShipment({
        shipmentUid: currentShipment.shipmentUid,
        deliveryReportUid,
      })
      const updated = res.data.shipment.visits.find((v) => v.id === savedVisitId)
      if (!updated) return
      setVisits((prev) =>
        mergeSavedVisit(
          prev,
          savedVisitId,
          updated.deliveryWork,
          updated.deliveryWorkAttachments ?? []
        )
      )
    },
    [currentShipment, deliveryReportUid]
  )

  const applyShipmentDetail = useCallback(
    (shipmentUid: string, detail: ShipmentDetail, options?: ComboBoxOption[]) => {
      setCurrentShipment({ shipmentUid, detail })
      // sequence_order 昇順に整列（moveVisit のインデックス算出の前提）
      setVisits(
        [...detail.visits]
          .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
          .map((v) => toVisit(v, options ?? deliveryOptionsRef.current))
      )
      // リロード時に同じ shipment を復元できるよう URL に shipmentUid を反映する
      history.replace(
        `${ROUTES.spDeliveryReportWork.replace(':deliveryReportUid', deliveryReportUid)}?shipmentUid=${shipmentUid}`
      )
      setPageState('has_shipment')
    },
    [history, deliveryReportUid]
  )

  useEffect(() => {
    const init = async () => {
      const driverPhone = deliveryReportStorage.getDriverPhone()
      if (!driverPhone) {
        history.replace(ROUTES.spDeliveryReport.replace(':deliveryReportUid', deliveryReportUid))
        return
      }

      // WHY: 付帯作業マスタはテナント取得と依存関係がない（deliveryReportUid のみ使用）ため並列で先行させ、
      // SP 回線での初期表示を 1RTT ぶん短縮する。
      // NOTE: テナント取得が失敗した場合はこの結果を await せず捨てるので、未処理 rejection 化を防ぐ
      // ダミーの catch を先に張っておく（await 側では元の Promise の成否がそのまま得られる）
      const workTypeSettingsPromise = getDeliveryWorkTypeSettings(deliveryReportUid)
      workTypeSettingsPromise.catch(() => undefined)

      try {
        const tenantRes = await getDeliveryReportTenant(deliveryReportUid)
        setTenantName(formatDeliveryReportTenantName(tenantRes.data))
        setDeliveryTenants(tenantRes.data.deliveryTenants)

        // WHY: 付帯作業マスタの取得失敗はページ全体を落とさない（チップが出ないだけで
        // 打刻・メモ・添付は使える）。通知して継続する。
        // NOTE: 文言は BE メッセージでなく文脈付き固定にする（バックグラウンド取得のため
        // 「何の取得に失敗したか」が要る。(C) の 404 文言はこの画面文脈では誤解を生む）
        try {
          const workTypesRes = await workTypeSettingsPromise
          setWorkTypeSettings(workTypesRes.data.workTypeSettings)
        } catch {
          notify({ message: '付帯作業マスタの取得に失敗しました。', variant: 'error' })
        }
        // setState は非同期で ref に即反映されないため、本 init 内の applyShipmentDetail には
        // 取得した選択肢を明示的に渡してラベル解決する。
        const options = toDeliveryDestinationOptions(tenantRes.data.deliveryTenants)

        if (isStartNew) {
          history.replace(
            ROUTES.spDeliveryReportWork.replace(':deliveryReportUid', deliveryReportUid)
          )
          setPageState('no_shipment')
          return
        }

        // 一覧からの遷移で shipmentUid が URL にある場合は直接取得
        if (shipmentUidFromUrl) {
          const detailRes = await getShipment({
            shipmentUid: shipmentUidFromUrl,
            deliveryReportUid,
          })
          applyShipmentDetail(shipmentUidFromUrl, detailRes.data.shipment, options)
          return
        }

        const shipmentUids = deliveryReportStorage.getShipmentUids()
        if (shipmentUids.length > 0) {
          const searchRes = await searchShipments({
            deliveryReportUid,
            driverPhone,
            shipmentUids,
            status: SHIPMENT_STATUS_DELIVERING,
          })
          if (searchRes.data.shipments.length > 0) {
            const found = searchRes.data.shipments[0]
            const detailRes = await getShipment({
              shipmentUid: found.shipmentUid,
              deliveryReportUid,
            })
            applyShipmentDetail(found.shipmentUid, detailRes.data.shipment, options)
            return
          }
        }

        setPageState('no_shipment')
      } catch (error) {
        if (error instanceof TruckbookApiResponseError && error.httpStatus === 404) {
          setPageState('invalid_uid')
        } else {
          setPageState('network_error')
        }
      }
    }

    init()
    // isStartNew は useState 初期値で固定のため、deps に含めても再実行されない
  }, [deliveryReportUid, history, isStartNew, shipmentUidFromUrl, applyShipmentDetail, notify])

  useEffect(() => {
    if (!currentShipment) return
    const existingPickBookingId = currentShipment.detail.pickBookingId
    const existingPickBooking = currentShipment.detail.pickBooking
    let ignore = false

    setPickBookingCandidates([])
    setPickBookingId(null)
    setIsCandidatesLoading(true)

    const fetchCandidates = async () => {
      const driverPhone = deliveryReportStorage.getDriverPhone()
      if (!driverPhone) {
        if (!ignore) setIsCandidatesLoading(false)
        return
      }
      try {
        const res = await getBookingCandidates({ deliveryReportUid, driverPhone })
        if (ignore) return
        const candidates = res.data.bookings
        const isInCandidates = candidates.some((c) => c.bookingId === existingPickBookingId)
        // 24時間超の選択済み booking は candidates API に含まれないが、
        // getShipment の pick_booking フィールドから取得できるため先頭に追加する
        // != null で undefined も除外（BE が古い場合 pick_booking フィールド自体が存在しないため）
        const mergedCandidates =
          existingPickBooking != null && !isInCandidates
            ? [existingPickBooking, ...candidates]
            : candidates
        setPickBookingCandidates(mergedCandidates)
        // options に存在する場合のみ選択値をセットする。
        // booking 削除済み（pick_booking = null）等で候補に出せない場合は未選択にし、
        // SpSelect の value と option の不整合（React の controlled select 警告）を防ぐ。
        const isSelectable = mergedCandidates.some((c) => c.bookingId === existingPickBookingId)
        setPickBookingId(isSelectable ? existingPickBookingId : null)
      } catch (error) {
        if (ignore) return
        notify({ message: getTruckbookApiErrorMessage(error), variant: 'error' })
        setPickBookingCandidates([])
      } finally {
        if (!ignore) setIsCandidatesLoading(false)
      }
    }
    fetchCandidates()
    return () => {
      ignore = true
    }
    // currentShipment.shipmentUid が変わったとき（新規 shipment 取得時）のみ再実行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShipment?.shipmentUid, deliveryReportUid, notify])

  const handleStartDelivery = useCallback(async () => {
    const driverPhone = deliveryReportStorage.getDriverPhone()
    if (!driverPhone || isCreating) return

    setIsCreating(true)
    try {
      const created = await createShipment({ deliveryReportUid, driverPhone })
      const { shipmentUid } = created.data.shipment
      deliveryReportStorage.appendShipmentUid(shipmentUid)
      const detailRes = await getShipment({ shipmentUid, deliveryReportUid })
      applyShipmentDetail(shipmentUid, detailRes.data.shipment)
    } catch (error) {
      notify({ message: getTruckbookApiErrorMessage(error), variant: 'error' })
    } finally {
      setIsCreating(false)
    }
  }, [deliveryReportUid, isCreating, notify, applyShipmentDetail])

  const handleNewDeliveryFromMenu = useCallback(() => {
    setCurrentShipment(null)
    setPageState('no_shipment')
  }, [])

  const pickBookingOptions = useMemo(
    () =>
      pickBookingCandidates.map((c) => ({
        label: formatBookingLabel(c),
        value: c.bookingId,
      })),
    [pickBookingCandidates]
  )

  return {
    snackbar,
    notify,
    pageState,
    tenantName,
    deliveryReportUid,
    shipmentUid: currentShipment?.shipmentUid ?? '',
    driverPhone: deliveryReportStorage.getDriverPhone() ?? '',
    currentShipment,
    isCreating,
    pickBookingId,
    pickBookingOptions,
    isCandidatesLoading,
    isPickBookingUpdating,
    visits,
    workTypeSettings,
    deliveryDestinationOptions,
    deliveryHistoryValues,
    isStamping,
    isMutatingVisit,
    handleStartDelivery,
    handleNewDeliveryFromMenu,
    handlePickBookingChange,
    handleDeliveryTenantChange,
    handleSelectDelivery,
    handleClearDelivery,
    handleAddVisit,
    handleStampClick,
    handleDeleteVisit,
    handleMoveVisitUp,
    handleMoveVisitDown,
    isDeliveryCompleted:
      currentShipment !== null && currentShipment.detail.status === SHIPMENT_STATUS_COMPLETED,
    isDeliveryProcessing,
    confirmType,
    handleCompleteDelivery,
    handleCancelDeliveryComplete,
    handleDeliveryConfirmOk,
    handleDeliveryConfirmClose,
    handleDeliveryWorkSaved,
    hasUnsavedChanges,
    reportVisitDirty,
  }
}
